import { useCallback } from 'react';
import { toast } from 'sonner';
import { useMarkAgentUnread } from '@/hooks/agents/useAgentViews';
import type { Agent } from '@/lib/types';

/**
 * Mark a run unread for the current user, with the confirmation and failure
 * toasts.
 *
 * Mirrors {@link useApplyAgentMark}: the sidebar row's menu and the agent
 * action list both go through here, so the copy and the error handling can't
 * drift between the two places the action is offered. Unlike a triage mark,
 * this is per-user state — nobody else's list changes.
 */
export function useApplyAgentUnread(): (agent: Pick<Agent, 'id'>) => void {
  const markUnread = useMarkAgentUnread();
  return useCallback(
    (agent) =>
      markUnread.mutate(agent.id, {
        onSuccess: () => toast.success('Marked unread'),
        onError: (err: Error) => toast.error('Failed to mark unread', { description: err.message }),
      }),
    [markUnread],
  );
}
